import { useState } from 'react';
import {
  CheckSquare,
  Calendar,
  PenLine,
  Settings2,
  Tag,
  TextQuote,
  Type,
  Baseline,
} from 'lucide-react';
import type { InputType } from '../lib/types';
import { useEditor } from './editorStore';
import { useVariables } from '../components/variables';

/** The signer-field tools — also Quick send's toolbar. */
export const INPUT_TOOLS: Array<{
  label: string;
  type: InputType;
  icon: React.ComponentType<{ size?: number }>;
}> = [
  { label: 'Signature', type: 'signature', icon: PenLine },
  { label: 'Initials', type: 'initials', icon: Baseline },
  { label: 'Date', type: 'date', icon: Calendar },
  { label: 'Text', type: 'text', icon: Type },
  { label: 'Checkbox', type: 'checkbox', icon: CheckSquare },
];

export function ToolPalette() {
  const addField = useEditor((s) => s.addField);
  const roles = useEditor((s) => s.roles);
  const openLibrary = useEditor((s) => s.setLibraryOpen);
  const { data: vars } = useVariables();
  const [q, setQ] = useState('');

  const needle = q.trim().toLowerCase();
  const list = (vars ?? []).filter(
    (v) => !needle || v.key.includes(needle) || v.label.toLowerCase().includes(needle),
  );

  return (
    <div className="flex w-52 flex-col gap-2 overflow-y-auto border-r border-slate-200 bg-white p-3">
      <div className="px-1 text-xs font-semibold uppercase tracking-wider text-slate-400">
        Variables
      </div>
      {(vars?.length ?? 0) > 6 && (
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search…"
          className="rounded-lg border border-slate-200 px-2 py-1 text-sm outline-none focus:border-brand-300"
        />
      )}
      <div className="flex max-h-56 flex-col gap-1 overflow-y-auto">
        {list.map((v) => (
          <button
            key={v.id}
            onClick={() => addField('variable', v.type, v.key)}
            title={`Place ${v.key} on the current page (can be placed multiple times)`}
            className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-left text-sm text-slate-700 transition-colors hover:border-brand-300 hover:bg-brand-50"
          >
            <Tag size={14} className="shrink-0" />
            <span className="truncate">{v.label}</span>
            {v.required && <span className="ml-auto text-red-400">*</span>}
          </button>
        ))}
        {vars && vars.length === 0 && (
          <p className="px-1 text-[11px] leading-relaxed text-slate-400">
            No variables in the library yet — add one below.
          </p>
        )}
      </div>
      <button
        onClick={() => openLibrary(true)}
        className="flex items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-1.5 text-sm text-slate-500 transition-colors hover:border-brand-300 hover:text-brand-700"
      >
        <Settings2 size={14} /> Manage library
      </button>

      <div className="mt-2 px-1 text-xs font-semibold uppercase tracking-wider text-slate-400">
        Fixed text
      </div>
      <button
        onClick={() => addField('static')}
        title="Text baked into the template — identical on every document, nothing to send"
        className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 transition-colors hover:border-brand-300 hover:bg-brand-50"
      >
        <TextQuote size={16} />
        Static text
      </button>

      <div className="mt-2 px-1 text-xs font-semibold uppercase tracking-wider text-slate-400">
        Signer fields
      </div>
      {INPUT_TOOLS.map((t) => {
        const Icon = t.icon;
        const needsRole = roles.length === 0;
        return (
          <button
            key={t.label}
            onClick={() => addField('input', t.type)}
            disabled={needsRole}
            title={needsRole ? 'Add a signer role first (Settings)' : `Add ${t.label}`}
            className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 transition-colors hover:border-brand-300 hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Icon size={16} />
            {t.label}
          </button>
        );
      })}
      <p className="mt-2 px-1 text-[11px] leading-relaxed text-slate-400">
        Fields are placed on the current page. Drag to move, drag edges to resize.
      </p>
    </div>
  );
}
